// Game settings for Conquer the Flag, shared by the site's waiting room and the CTF playground:
// a win-condition group (the first flag home wins; these can end it sooner), teams, and map conditions.

import React from 'react';
import { DEFAULT_CONFIG, EFFECT_NAMES, OUTPOST_NAMES, squadCost, ABILITY_NAMES, abilityText } from '../../../games/capture-the-flag/capture-the-flag';
import { BUILT_IN_TEAMS, SavedTeam, loadSavedTeams, saveTeam, removeTeam } from './teamLibrary';
import { decodeTeam } from '../../../games/capture-the-flag/teamcode';
import { CTF_ROOM_DEFAULT_CONFIG, MERCY_OPTIONS, FLAG_WEIGHT_MAX, DOMINATION_DEFAULT, DOMINATION_MAX } from '../../../games/capture-the-flag/room';
import type { CtfRoomConfig } from '../../../games/capture-the-flag/room';
import type { AbilityId, EffectId, OutpostLayout } from '../../../games/capture-the-flag/types';

const group: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid rgba(128,128,128,.35)', borderRadius: 8, padding: '8px 12px 12px', margin: 0 };
const legend: React.CSSProperties = { padding: '0 4px', fontWeight: 600 };

// One side's team: a dropdown of the standard squads and saved teams, and Upload (save a team code
// under a name and pick it). The game gets the team's code; '' plays the standard squad.
export function TeamPicker({ side, name, value, library, onLibrary, onChange }: {
  side: string; name: string; value: string; library: SavedTeam[];
  onLibrary: (l: SavedTeam[]) => void; onChange: (code: string) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newCode, setNewCode] = React.useState('');
  const [error, setError] = React.useState('');
  const teams = [...BUILT_IN_TEAMS, ...library];
  const code = value || BUILT_IN_TEAMS[0].code;
  const current = teams.find(t => t.code === code);
  let summary = '';
  try {
    const t = decodeTeam(code);
    summary = `${t.squad.map(u => u.name).join(', ')} (${squadCost(t.squad)} points)`;
  } catch (e) {
    summary = `${(e as Error).message} The standard squad plays instead.`;
  }
  const upload = () => {
    try {
      const entry = saveTeam(newName, newCode);
      onLibrary(loadSavedTeams());
      onChange(entry.code);
      setUploading(false); setNewName(''); setNewCode(''); setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const id = `ctf-${name}`;
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <label htmlFor={id}>{side} team{' '}
        <select id={id} name={name} value={code} onChange={e => onChange(e.target.value === BUILT_IN_TEAMS[0].code ? '' : e.target.value)}>
          {teams.map(t => <option key={t.name} value={t.code}>{t.name}</option>)}
          {!current && <option value={code}>From this room: {(() => { try { return decodeTeam(code).name; } catch { return 'unreadable'; } })()}</option>}
        </select>{' '}
        <button type="button" onClick={() => { setUploading(!uploading); setError(''); }}>{uploading ? 'Cancel' : 'Upload team'}</button>
        {current && !current.builtIn && (
          <>{' '}<button type="button" onClick={() => { removeTeam(current.name); onLibrary(loadSavedTeams()); onChange(''); }}>Remove</button></>
        )}
      </label>
      <small style={{ opacity: 0.75 }}>{summary}</small>
      {uploading && (
        <div style={{ display: 'grid', gap: 6, padding: 8, border: '1px solid rgba(128,128,128,.35)', borderRadius: 6 }}>
          <label htmlFor={`${id}-name`}>Name{' '}
            <input id={`${id}-name`} type="text" value={newName} maxLength={40} placeholder="the code's own name if blank" onChange={e => setNewName(e.target.value)} />
          </label>
          <textarea id={`${id}-code`} rows={3} value={newCode} spellCheck={false} aria-label="Team code"
            placeholder="Paste a code from the Team Editor (ctf-team1:…)"
            style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, resize: 'vertical' }}
            onChange={e => setNewCode(e.target.value)} />
          <div><button type="button" onClick={upload} disabled={!newCode.trim()}>Save and use for {side}</button></div>
          {error && <small style={{ color: '#f87171' }}>{error}</small>}
        </div>
      )}
    </div>
  );
}

// `hideTeams`: the site's room shows each side's team and ability next to its player instead.
// `hideDemo`: no demo option (a league's games are always played by their two players).
export default function CtfSettings({ config, onChange, hideTeams = false, hideDemo = false }: { config: CtfRoomConfig; onChange: (c: CtfRoomConfig) => void; hideTeams?: boolean; hideDemo?: boolean }) {
  const c = { ...CTF_ROOM_DEFAULT_CONFIG, ...config };
  const [library, setLibrary] = React.useState<SavedTeam[]>(loadSavedTeams);
  const set = (patch: CtfRoomConfig) => onChange({ ...c, ...patch });
  const centreChoices = Object.keys(EFFECT_NAMES) as EffectId[];
  const centre = centreChoices.includes(c.centre as EffectId) ? c.centre : '';
  return (
    <div className="ctf-settings" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <fieldset style={group}>
        <legend style={legend}>Win condition</legend>
        <p style={{ margin: 0 }}>The first team to bring the enemy flag home wins.</p>
        <label>
          <input name="domination" type="checkbox" aria-label="Domination" checked={(c.dominationCells ?? 0) > 0}
            onChange={e => set({ dominationCells: e.target.checked ? DOMINATION_DEFAULT : 0 })} />{' '}
          Domination: hold{' '}
          <input name="dominationCells" type="number" min={1} max={DOMINATION_MAX} style={{ width: 64 }}
            value={(c.dominationCells ?? 0) > 0 ? c.dominationCells : DOMINATION_DEFAULT} disabled={!((c.dominationCells ?? 0) > 0)}
            onChange={e => set({ dominationCells: Math.max(1, Math.min(DOMINATION_MAX, parseInt(e.target.value, 10) || DOMINATION_DEFAULT)) })} />{' '}
          cells at the end of a round to win
        </label>
        <label>
          Mercy rule{' '}
          <select name="mercyRounds" value={c.mercyRounds} onChange={e => set({ mercyRounds: Number(e.target.value) })}>
            <option value={0}>Off</option>
            {MERCY_OPTIONS.map(n => <option key={n} value={n}>After {n} rounds, more cells wins</option>)}
          </select>
        </label>
      </fieldset>
      {!hideDemo && <fieldset style={group}>
        <legend style={legend}>Players</legend>
        <label>
          <input name="demo" type="checkbox" checked={!!c.demo} onChange={e => set({ demo: e.target.checked })} />{' '}
          Demo: the host plays both sides and everyone else watches
        </label>
      </fieldset>}
      {!hideTeams && <fieldset style={group}>
        <legend style={legend}>Teams</legend>
        {(['A', 'B'] as const).map(t => {
          const [team, key] = t === 'A' ? ['teamA', 'abilityA'] as const : ['teamB', 'abilityB'] as const;
          const side = t === 'A' ? 'Blue' : 'Red';
          const v = (c[key] ?? '') as string;
          return (
            <div key={t} style={{ display: 'grid', gap: 4 }}>
              <TeamPicker side={side} name={team} value={c[team] ?? ''} library={library} onLibrary={setLibrary} onChange={code => set({ [team]: code })} />
              <label>{side} army ability{' '}
                <select name={key} value={v} onChange={e => set({ [key]: e.target.value })}>
                  <option value="">None</option>
                  {(Object.keys(ABILITY_NAMES) as AbilityId[]).map(id => <option key={id} value={id}>{ABILITY_NAMES[id]}</option>)}
                </select>
              </label>
              {v && <small style={{ opacity: 0.75 }}>{abilityText[v as AbilityId]}</small>}
            </div>
          );
        })}
      </fieldset>}
      <fieldset style={group}>
        <legend style={legend}>Map conditions</legend>
        <label>
          Outposts{' '}
          <select name="outposts" value={c.outposts} onChange={e => set({ outposts: e.target.value as OutpostLayout })}>
            {(Object.keys(OUTPOST_NAMES) as OutpostLayout[]).map(k => <option key={k} value={k}>{OUTPOST_NAMES[k]}</option>)}
          </select>
        </label>
        {c.outposts !== 'none' && (
          <label>
            Center outpost{' '}
            <select name="centre" value={centre} onChange={e => set({ centre: e.target.value })}>
              <option value="">Random ({DEFAULT_CONFIG.effectPool.map(e => EFFECT_NAMES[e]).join(' or ')})</option>
              {centreChoices.map(e => <option key={e} value={e}>{EFFECT_NAMES[e]}</option>)}
            </select>
          </label>
        )}
        <label>
          Prisons{' '}
          <select name="prisons" value={c.prisons ? 'on' : ''} onChange={e => set({ prisons: e.target.value })}>
            <option value="">Off: tagged units go back to their teleporter</option>
            <option value="on">On: tagged on enemy ground, to prison until freed</option>
          </select>
        </label>
        <label>
          Flag weight{' '}
          <input name="flagWeight" type="number" min={0} max={FLAG_WEIGHT_MAX} step={1} style={{ width: '3.5em' }}
            value={c.flagWeight ?? DEFAULT_CONFIG.carrierMovePenalty}
            onChange={e => set({ flagWeight: Math.max(0, Math.min(FLAG_WEIGHT_MAX, Math.round(Number(e.target.value) || 0))) })} />{' '}
          less Move for the flag carrier (at least 1 left; 0 = off)
        </label>
      </fieldset>
    </div>
  );
}
